
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Truck, Users, FileText, BarChart3, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SupplyChainOverview } from './SupplyChainOverview';
import { SupplierManager } from './SupplierManager';
import { AssessmentManager } from './AssessmentManager';
import { SupplyChainAnalytics } from './SupplyChainAnalytics';

export const SupplyChainMain = () => {
  const [refreshData, setRefreshData] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { value: 'overview', label: 'Overview', icon: BarChart3 },
    { value: 'suppliers', label: 'Suppliers', icon: Users },
    { value: 'assessments', label: 'Assessments', icon: FileText },
    { value: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Overview';

  const handleDataUpdate = () => {
    setRefreshData(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Supply Chain Assessment
          </CardTitle>
          <p className="text-gray-600">
            Manage supplier relationships and assess supply chain sustainability performance
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            {/* Desktop Tabs List */}
            <TabsList className="hidden md:grid w-full grid-cols-4">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Mobile Tabs Dropdown */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                    <Menu className="h-5 w-5 text-gray-500" />
                    <span className="flex items-center gap-2 border-l pl-3">
                      {React.createElement(tabs.find(t => t.value === activeTab)?.icon || BarChart3, { className: "h-5 w-5" })}
                      <span className="font-semibold">{activeTabLabel}</span>
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="start">
                  {tabs.map((tab) => (
                    <DropdownMenuItem
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className="flex items-center gap-2 py-3"
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TabsContent value="overview">
              <SupplyChainOverview key={refreshData} />
            </TabsContent>

            <TabsContent value="suppliers">
              <SupplierManager onUpdate={handleDataUpdate} />
            </TabsContent>

            <TabsContent value="assessments">
              <AssessmentManager onUpdate={handleDataUpdate} />
            </TabsContent>

            <TabsContent value="analytics">
              <SupplyChainAnalytics key={refreshData} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
